/**
 * @file api-shop-avito-finance.ts
 * @description API-клиент Avito Финансы (phase14.0.4).
 *
 * Эндпоинты:
 *  - GET  /admin/shop/avito/finance/snapshot
 *  - POST /admin/shop/avito/finance/fetch
 *  - GET  /admin/shop/avito/finance/operations
 *  - GET  /admin/shop/avito/finance/settings
 *  - PUT  /admin/shop/avito/finance/settings
 */

import { apiFetch } from "./api-client";

export interface AvitoFinanceSnapshotDto {
  integrationId: string;
  configured: boolean;
  sellerUserId: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  profileUrl: string | null;
  /** BigInt в копейках, передаётся строкой. */
  walletRealPenny: string;
  walletBonusPenny: string;
  cpaBalancePenny: string;
  balanceFetchedAt: string | null;
  lastFetchedAt: string | null;
}

export interface AvitoFinanceOperationDto {
  id: string;
  serviceType: string;
  serviceName: string;
  operationType: string;
  operationName: string;
  amountRubPenny: string;
  amountBonusPenny: string;
  amountTotalPenny: string;
  occurredAt: string;
  fetchedAt: string;
  archivedAt: string | null;
}

export interface AvitoFinanceSettingsDto {
  integrationId: string;
  historyRetentionDays: number;
  archiveOlderThanDays: number;
  autoFetchEnabled: boolean;
  autoFetchPeriodDays: number;
}

export interface AvitoFinanceFetchResultDto {
  profile: { sellerUserId: string } | null;
  balance: { walletRealPenny: string; walletBonusPenny: string } | null;
  operations: { fetched: number; created: number; chunks: number } | null;
}

export interface AvitoFinanceFetchInput {
  profile?: boolean;
  balance?: boolean;
  /** Если задано — операции тянутся за этот период. */
  operations?: { from: string; to: string };
}

export interface AvitoFinanceOperationsQuery {
  from?: string;
  to?: string;
  serviceType?: string;
  includeArchived?: boolean;
  page?: number;
  pageSize?: number;
}

export const shopAvitoFinance = {
  snapshot: () => apiFetch<AvitoFinanceSnapshotDto>("/admin/shop/avito/finance/snapshot"),

  fetch: (input: AvitoFinanceFetchInput) =>
    apiFetch<AvitoFinanceFetchResultDto>("/admin/shop/avito/finance/fetch", {
      method: "POST",
      json: input,
    }),

  operations: (q: AvitoFinanceOperationsQuery = {}) => {
    const search = new URLSearchParams();
    if (q.from) search.set("from", q.from);
    if (q.to) search.set("to", q.to);
    if (q.serviceType) search.set("serviceType", q.serviceType);
    if (q.includeArchived) search.set("includeArchived", "true");
    if (q.page) search.set("page", String(q.page));
    if (q.pageSize) search.set("pageSize", String(q.pageSize));
    const qs = search.toString();
    return apiFetch<{ items: AvitoFinanceOperationDto[]; total: number }>(
      `/admin/shop/avito/finance/operations${qs ? `?${qs}` : ""}`,
    );
  },

  getSettings: () =>
    apiFetch<AvitoFinanceSettingsDto>("/admin/shop/avito/finance/settings"),

  updateSettings: (data: Partial<Omit<AvitoFinanceSettingsDto, "integrationId">>) =>
    apiFetch<AvitoFinanceSettingsDto>("/admin/shop/avito/finance/settings", {
      method: "PUT",
      json: data,
    }),
};

/** Форматирование копеек в рубли для UI («1 234.56 ₽»). */
export function formatPennyToRub(penny: string | bigint | number): string {
  const n = typeof penny === "string" ? BigInt(penny) : typeof penny === "number" ? BigInt(penny) : penny;
  const rub = Number(n) / 100;
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rub);
}

/** Цвет/иконка для service-типа (для бейджа в таблице операций). */
export function getServiceTypeStyle(serviceType: string): {
  label: string;
  className: string;
} {
  switch (serviceType.toLowerCase()) {
    case "cpa":
      return { label: "CPA", className: "bg-blue-100 text-blue-800" };
    case "vas":
    case "vip":
    case "premium":
    case "boost":
      return { label: "Продвижение", className: "bg-purple-100 text-purple-800" };
    case "withdrawal":
      return { label: "Вывод", className: "bg-amber-100 text-amber-800" };
    default:
      return { label: serviceType, className: "bg-gray-100 text-gray-800" };
  }
}
